import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import ContributorDetails from '../../components/ContributorDetails';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import type { Sponsorship } from '../../components/SlidingBanner';
import {
  type AvatarInfo,
  getAllAvatars,
  getLocalizedAvatarInfo,
  type ResponsiveImage,
} from '../../config/avatarMapping';
import { getIdToken, isAuthenticated } from '../../services/cognitoService';
import { purchaseSponsorship } from '../../services/revenuecatService';
import {
  type AvailableShaab,
  getAllSponsorshipsPublic,
  getAvailableShaabsPublic,
} from '../../services/sponsorService';

import { logger } from '../../utils/logger';
import './Sponsor.css';

interface SponsorCompanion {
  id: string;
  name: string;
  arabicName: string;
  description: string;
  image: string;
  images?: ResponsiveImage;
  sponsorshipPrice: number;
  isSponsored: boolean;
  sponsorInfo?: {
    displayName: string;
    notes: string | null;
    fundedAt: string;
  };
}

/** Returns how many sponsor cards to show per page based on viewport width */
const getCompanionsPerPage = (width: number): number => {
  if (width > 1024) return 3;
  if (width > 768) return 2;
  return 1;
};

const Sponsor: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isLoggedIn = isAuthenticated();

  const [donatingCompanionId, setDonatingCompanionId] = useState<string | null>(null);

  // Grid/Paging state - restored from original
  const [currentPage, setCurrentPage] = useState(0);
  const [companionsPerPage, setCompanionsPerPage] = useState(
    getCompanionsPerPage(window.innerWidth),
  );

  const [selectedSponsorPopup, setSelectedSponsorPopup] = useState<{
    avatarName: string;
    displayName: string;
    notes: string | null;
    fundedAt: string;
  } | null>(null);

  // Sponsorship form state
  const [showSponsorshipForm, setShowSponsorshipForm] = useState(false);
  const [sponsorDisplayName, setSponsorDisplayName] = useState('');
  const [sponsorNotes, setSponsorNotes] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // Filter state
  const [activeFilter, setActiveFilter] = useState<'all' | 'available' | 'donated'>('all');

  // Carousel refs for trackpad support and scrolling
  const carouselRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const hasRestoredFocus = useRef(false);
  const isFirstMount = useRef(true);
  const wheelAccumulator = useRef(0);
  const wheelCooldown = useRef(false);

  // Touch refs for mobile swiping
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isSwiping = useRef(false);

  // Real sponsor data from the public API
  const [sponsorships, setSponsorships] = useState<Sponsorship[]>([]);
  // Real available/sponsored list from the public API
  const [sponsoredShaabIds, setSponsoredShaabIds] = useState<Set<string>>(new Set());

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  // Fetch sponsor data — public endpoints, no auth required
  const fetchSponsorData = useCallback(async () => {
    try {
      const [availableResponse, allResponse] = await Promise.all([
        getAvailableShaabsPublic(),
        getAllSponsorshipsPublic(),
      ]);

      // Build sponsored ID set from the sponsored list for accurate isSponsored lookup
      setSponsoredShaabIds(new Set(availableResponse.sponsored.map((s: AvailableShaab) => s.id)));

      // Real sponsorship notes for the banner
      setSponsorships(allResponse.sponsorships);
    } catch (error) {
      logger.error('Sponsor', 'Failed to fetch sponsor data', error as Error);
    }
  }, []);

  useEffect(() => {
    fetchSponsorData();
  }, [fetchSponsorData]);

  // Get companions from avatar mapping, merging with real sponsor status from API
  const companions: SponsorCompanion[] = useMemo(() => {
    // Create a map of sponsored avatars for quick lookup (for sponsor popup info)
    const sponsoredMap = new Map(sponsorships.map((s) => [s.avatarId, s.sponsor]));

    return getAllAvatars().map((avatar: AvatarInfo) => {
      const displayInfo = getLocalizedAvatarInfo(avatar, i18n.language);
      // Use API-provided sets for accurate available/sponsored status
      const isSponsored = sponsoredShaabIds.has(avatar.id);
      const sponsorInfo = sponsoredMap.get(avatar.id);
      return {
        id: avatar.id,
        name: displayInfo.displayName,
        arabicName: displayInfo.arabicName,
        description:
          displayInfo.displayDescription ??
          t(
            'sponsorPage.companionDescription',
            'Every response is grounded in classical Islamic texts with precise citations to hadith collections, historical accounts...',
          ),
        image: avatar.image,
        images: avatar.images,
        sponsorshipPrice: 2500,
        isSponsored,
        sponsorInfo,
      };
    });
  }, [sponsorships, sponsoredShaabIds, i18n.language, t]);

  // Filter companions based on active filter using real API data
  const filteredCompanions = useMemo(() => {
    switch (activeFilter) {
      case 'available':
        return companions.filter((c) => !c.isSponsored);
      case 'donated':
        return companions.filter((c) => c.isSponsored);
      default:
        return companions;
    }
  }, [companions, activeFilter]);

  const totalPages = Math.ceil(filteredCompanions.length / companionsPerPage);

  const currentCompanions = useMemo(() => {
    return filteredCompanions.slice(
      currentPage * companionsPerPage,
      (currentPage + 1) * companionsPerPage,
    );
  }, [filteredCompanions, currentPage, companionsPerPage]);

  // Handle resizing
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setCompanionsPerPage(getCompanionsPerPage(width));
      setIsMobile(width <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Separate Effect for initial focus restoration to wait for data
  useEffect(() => {
    if (hasRestoredFocus.current || filteredCompanions.length === 0) return;

    const lastCompanionId = sessionStorage.getItem('lastVisitedCompanion');
    if (lastCompanionId) {
      const companionIndex = filteredCompanions.findIndex((c) => c.id === lastCompanionId);
      if (companionIndex >= 0) {
        // Set the correct page
        const targetPage = Math.floor(companionIndex / companionsPerPage);
        setCurrentPage(targetPage);

        // Scroll to the entire section to keep filters (top) and pagination (bottom) visible
        setTimeout(() => {
          if (sectionRef.current) {
            // Centering the entire section is the best way to keep both top/bottom visible if they fit
            sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Remove the flag after successful scroll
            sessionStorage.removeItem('lastVisitedCompanion');
          }
        }, 600); // 600ms to be safe for data and DOM
      }
      hasRestoredFocus.current = true;
    }
  }, [filteredCompanions, companionsPerPage]);

  // Reset to first page when filter changes, but NOT on initial mount/restoration
  // biome-ignore lint/correctness/useExhaustiveDependencies: Explicitly trigger on filter change
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    // If we just restored focus, don't reset
    if (sessionStorage.getItem('lastVisitedCompanion')) return;

    setCurrentPage(0);
  }, [activeFilter]);

  const handlePrev = useCallback(() => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNext = useCallback(() => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  }, [totalPages]);

  // Trackpad / Wheel and Touch support for paging
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    const onNativeWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) return;
      e.preventDefault();
      if (wheelCooldown.current) return;

      wheelAccumulator.current += e.deltaX;
      const SWIPE_THRESHOLD = 50;

      if (wheelAccumulator.current > SWIPE_THRESHOLD) {
        handleNext();
        wheelCooldown.current = true;
        setTimeout(() => {
          wheelCooldown.current = false;
        }, 500);
        wheelAccumulator.current = 0;
      } else if (wheelAccumulator.current < -SWIPE_THRESHOLD) {
        handlePrev();
        wheelCooldown.current = true;
        setTimeout(() => {
          wheelCooldown.current = false;
        }, 500);
        wheelAccumulator.current = 0;
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
      isSwiping.current = true;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isSwiping.current) return;

      const deltaX = e.touches[0].clientX - touchStartX.current;
      const deltaY = e.touches[0].clientY - touchStartY.current;

      // If horizontal swipe is clearly dominant, prevent page bounce
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        if (e.cancelable) e.preventDefault();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isSwiping.current) return;

      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      const deltaX = touchEndX - touchStartX.current;
      const deltaY = touchEndY - touchStartY.current;
      const SWIPE_THRESHOLD = 50;

      // Only pager if horizontal swipe is significant and dominant
      if (Math.abs(deltaX) > SWIPE_THRESHOLD && Math.abs(deltaX) > Math.abs(deltaY)) {
        if (deltaX < 0) {
          handleNext();
        } else {
          handlePrev();
        }
      }

      isSwiping.current = false;
    };

    el.addEventListener('wheel', onNativeWheel, { passive: false });
    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('wheel', onNativeWheel);
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleNext, handlePrev]);

  // Handle click on Sahaba image - navigate to SPONSOR info page
  const handleAvatarClick = (id: string) => {
    sessionStorage.setItem('lastVisitedCompanion', id);
    navigate(`/sponsor/${id}`);
  };

  const handleDonate = (companionId: string) => {
    if (!isLoggedIn) {
      navigate('/login', { state: { from: '/sponsor' } });
      return;
    }
    setSponsorDisplayName('');
    setSponsorNotes('');
    setIsAnonymous(false);
    setPaymentError(null);
    setDonatingCompanionId(companionId);
    setShowSponsorshipForm(true);
  };

  const handleShowSponsorInfo = (e: React.MouseEvent, companion: SponsorCompanion) => {
    e.stopPropagation();
    if (companion.sponsorInfo) {
      setSelectedSponsorPopup({
        avatarName: companion.name,
        displayName: companion.sponsorInfo.displayName,
        notes: companion.sponsorInfo.notes,
        fundedAt: companion.sponsorInfo.fundedAt,
      });
    }
  };

  const handleSubmitSponsorship = async () => {
    if (!donatingCompanionId) return;

    const selectedCompanion = companions.find((c) => c.id === donatingCompanionId);
    if (!selectedCompanion) return;

    setIsProcessingPayment(true);
    setPaymentError(null);

    try {
      const authToken = await getIdToken();
      if (!authToken) {
        throw new Error('Not authenticated');
      }

      const result = await purchaseSponsorship(
        donatingCompanionId,
        selectedCompanion.name,
        isAnonymous ? undefined : sponsorDisplayName || undefined,
        sponsorNotes || undefined,
        !isAnonymous,
        authToken,
      );

      if (result.success) {
        // Close the form and refresh sponsorship data
        setShowSponsorshipForm(false);
        setDonatingCompanionId(null);
        fetchSponsorData();
        alert(result.message);
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'PURCHASE_CANCELLED') {
          // User cancelled, just close the form
          setShowSponsorshipForm(false);
        } else {
          setPaymentError(error.message);
        }
      } else {
        setPaymentError(t('sponsorPage.paymentFailed', 'Payment failed. Please try again.'));
      }
    } finally {
      setIsProcessingPayment(false);
    }
  };

  return (
    <div className="sponsor-page-wrapper">
      <div className="bg-accent-shape" />
      <Header isLoggedIn={isLoggedIn} />

      <main className="sponsor-main">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <button type="button" onClick={() => navigate(-1)} className="breadcrumb-back-btn">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <title>{t('common.back', 'Back')}</title>
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <div className="breadcrumb-path">
            <Link to="/" className="breadcrumb-item">
              {t('header.home')}
            </Link>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <title>Separator</title>
              <path d="M9 18l6-6-6-6" />
            </svg>
            <span className="breadcrumb-item active">{t('header.sponsor')}</span>
          </div>
        </div>

        {/* Hero Section */}
        <section className="sponsor-hero">
          <div className="sponsor-hero-content">
            <div className="hero-line"></div>
            <h2 className="hero-label">{t('sponsorPage.heroLabel', 'Sponsor a companion')}</h2>
          </div>
          <h1 className="hero-title">
            {t('sponsorPage.heroTitle', 'Help Us Preserve History and Dialogue')}
          </h1>
        </section>
        {/* Sliding Banner - Sponsor Notes - Commented out for now */}
        {/* {sponsorships.length > 0 && <SlidingBanner sponsorships={sponsorships} />} */}

        {/* Select Companion Section */}
        <section className="sponsor-select-section" ref={sectionRef}>
          <div className="section-header">
            <div className="header-line"></div>
            <h2 className="section-title">
              {t('sponsorPage.selectCompanion', 'Select a companion to support')}
            </h2>
          </div>

          {/* Filter Buttons - open to all users, no login required */}
          <div className="sponsor-filter-section">
            <span className="filter-label">
              {t('sponsorPage.filterByCategory', 'FILTER BY CATEGORY')}
            </span>
            <div className="sponsor-filters">
              <button
                type="button"
                className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
                onClick={() => setActiveFilter('all')}
              >
                {t('sponsorPage.filterAll', 'All')}
              </button>
              <button
                type="button"
                className={`filter-btn ${activeFilter === 'available' ? 'active' : ''}`}
                onClick={() => setActiveFilter('available')}
              >
                {t('sponsorPage.filterAvailable', 'Available')}
              </button>
              <button
                type="button"
                className={`filter-btn ${activeFilter === 'donated' ? 'active' : ''}`}
                onClick={() => setActiveFilter('donated')}
              >
                {t('sponsorPage.filterDonated', 'Sponsored')}
              </button>
            </div>
          </div>

          {/* Companion Cards with Navigation */}
          <div className="sponsor-carousel-wrapper" ref={carouselRef}>
            {/* Left Arrow */}
            {!isMobile && (
              <button
                type="button"
                className="sponsor-nav-btn sponsor-nav-prev"
                onClick={handlePrev}
                disabled={currentPage === 0}
                aria-label={t('common.previous', 'Previous')}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <title>{t('common.previous', 'Previous')}</title>
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
            )}

            <div className="sponsor-cards-container">
              {currentCompanions.map((companion) => (
                <div
                  key={companion.id}
                  id={`companion-${companion.id}`}
                  className="sponsor-card-wrapper"
                >
                  {/* Card with Image */}
                  <div className={`sponsor-card ${companion.isSponsored ? 'sponsored' : ''}`}>
                    {/* Avatar/Image */}
                    <div className="sponsor-card-avatar">
                      <button
                        type="button"
                        className="sponsor-card-img-btn"
                        onClick={() => handleAvatarClick(companion.id)}
                        aria-label={t(
                          'sponsor.labels.learnMoreAbout',
                          'Learn more about {{name}}',
                          { name: companion.name },
                        )}
                      >
                        <img
                          src={companion.images?.medium || companion.image}
                          alt={companion.name}
                          loading="lazy"
                        />
                      </button>
                    </div>

                    {/* Sponsored Badge */}
                    {companion.isSponsored && (
                      <button
                        type="button"
                        className="sponsored-badge"
                        onClick={(e) => handleShowSponsorInfo(e, companion)}
                        aria-label={t('sponsorPage.viewSponsorInfo', 'View sponsor info')}
                      >
                        <span>{t('sponsorPage.sponsored', 'Sponsored')}</span>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden="true"
                        >
                          <title>Info</title>
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="16" x2="12" y2="12" />
                          <line x1="12" y1="8" x2="12.01" y2="8" />
                        </svg>
                      </button>
                    )}

                    {/* Card Info */}
                    <div className="sponsor-card-info">
                      <h3 className="companion-name">{companion.name}</h3>
                      <p className="companion-description">{companion.description}</p>
                    </div>
                  </div>

                  {/* Button Container */}
                  <div className="sponsor-card-buttons">
                    <button
                      type="button"
                      className="learn-more-btn"
                      onClick={() => handleAvatarClick(companion.id)}
                    >
                      {t('sponsorPage.learnMore', 'Learn More')}
                    </button>
                    <button
                      type="button"
                      className={`sponsor-btn ${companion.isSponsored ? 'disabled' : ''}`}
                      onClick={() => handleDonate(companion.id)}
                      disabled={companion.isSponsored}
                    >
                      {companion.isSponsored
                        ? t('sponsorPage.donated', 'Sponsored ✓')
                        : t('sponsorPage.donateButton', 'Sponsor')}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Right Arrow */}
            {!isMobile && (
              <button
                type="button"
                className="sponsor-nav-btn sponsor-nav-next"
                onClick={handleNext}
                disabled={currentPage === totalPages - 1}
                aria-label={t('common.next', 'Next')}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <title>{t('common.next', 'Next')}</title>
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            )}
          </div>

          {/* Pagination Dots */}
          <div className="sponsor-pagination">
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                type="button"
                key={`page-${index + 1}`}
                className={`pagination-dot ${currentPage === index ? 'active' : ''}`}
                onClick={() => setCurrentPage(index)}
                aria-label={t('common.page', 'Page {{page}}', { page: index + 1 })}
                aria-current={currentPage === index}
              />
            ))}
          </div>
        </section>
      </main>

      <Footer />

      {/* Contributor Info Popup */}
      <ContributorDetails
        isOpen={!!selectedSponsorPopup}
        onClose={() => setSelectedSponsorPopup(null)}
        contributor={selectedSponsorPopup}
      />

      {/* Sponsorship Form Modal */}
      {showSponsorshipForm && donatingCompanionId && (
        <button
          type="button"
          className="sponsorship-form-overlay"
          onClick={() => !isProcessingPayment && setShowSponsorshipForm(false)}
          aria-label={t('common.close', 'Close')}
        >
          <div
            className="sponsorship-form-modal"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              className="sponsorship-form-close"
              onClick={() => setShowSponsorshipForm(false)}
              disabled={isProcessingPayment}
              aria-label={t('common.close', 'Close')}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <title>{t('common.close', 'Close')}</title>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <h2 className="sponsorship-form-title">
              {t('sponsorPage.completeSponsorshipTitle', 'Complete Your Sponsorship')}
            </h2>

            <p className="sponsorship-form-subtitle">
              {t('sponsorPage.sponsoring', 'You are sponsoring')}{' '}
              <strong>{companions.find((c) => c.id === donatingCompanionId)?.name}</strong>
            </p>

            <div className="sponsorship-form-fields">
              <div className="sponsorship-form-field">
                <label htmlFor="sponsor-anonymous" className="sponsorship-checkbox-label">
                  <input
                    type="checkbox"
                    id="sponsor-anonymous"
                    checked={isAnonymous}
                    onChange={(e) => setIsAnonymous(e.target.checked)}
                    disabled={isProcessingPayment}
                  />
                  <span>{t('sponsorPage.keepAnonymous', 'Keep my sponsorship anonymous')}</span>
                </label>
              </div>

              {!isAnonymous && (
                <div className="sponsorship-form-field">
                  <label htmlFor="sponsor-name">
                    {t('sponsorPage.displayNameLabel', 'Display Name (optional)')}
                  </label>
                  <input
                    type="text"
                    id="sponsor-name"
                    value={sponsorDisplayName}
                    onChange={(e) => setSponsorDisplayName(e.target.value)}
                    placeholder={t(
                      'sponsorPage.displayNamePlaceholder',
                      'How you want to be recognized',
                    )}
                    maxLength={50}
                    disabled={isProcessingPayment}
                  />
                </div>
              )}

              <div className="sponsorship-form-field">
                <label htmlFor="sponsor-notes">
                  {t('sponsorPage.notesLabel', 'Add a note (optional)')}
                </label>
                <textarea
                  id="sponsor-notes"
                  value={sponsorNotes}
                  onChange={(e) => setSponsorNotes(e.target.value)}
                  placeholder={t(
                    'sponsorPage.notesPlaceholder',
                    'In memory of... / May this benefit...',
                  )}
                  maxLength={200}
                  rows={3}
                  disabled={isProcessingPayment}
                />
                <span className="sponsorship-form-char-count">{sponsorNotes.length}/200</span>
              </div>
            </div>

            {paymentError && <div className="sponsorship-form-error">{paymentError}</div>}

            <div className="sponsorship-form-actions">
              <button
                type="button"
                className="sponsorship-form-cancel"
                onClick={() => setShowSponsorshipForm(false)}
                disabled={isProcessingPayment}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                className="sponsorship-form-submit"
                onClick={handleSubmitSponsorship}
                disabled={isProcessingPayment}
              >
                {isProcessingPayment
                  ? t('sponsorPage.processing', 'Processing...')
                  : t('sponsorPage.payNow', 'Pay $2,500 USD')}
              </button>
            </div>
          </div>
        </button>
      )}
    </div>
  );
};

export default Sponsor;
